import type { ZappNavigationNavItem } from '~/services/layout/types';
import * as Dialog from '@radix-ui/react-dialog';
import React, { useEffect } from 'react';
import { tailwindStyles } from './styles';
import { WrapperContent } from './WrapperContent';
import { useLoaderData, useLocation } from '@remix-run/react';
import ToggleNavBarButton from './button';
import { useScreenWidth } from '~/utils/screen-width';
import { useDrawerStore } from './use-drawer-store';
import { getPluginConfig } from '~/utils/get-plugin-configuration';
import manifest from '../config/manifest';

type PropsWithNavItem = {
  navItem: ZappNavigationNavItem;
};

function getPortalZIndex({
  showOverNavbar,
  isPlayerScreen,
}: {
  showOverNavbar: boolean;
  isPlayerScreen: boolean;
}): string {
  if (showOverNavbar) {
    return isPlayerScreen ? 'z-[350]' : 'z-[250]';
  }

  return isPlayerScreen ? 'z-[250]' : 'z-40';
}

const DrawerRoot = ({
  children,
  ...props
}: Omit<Dialog.DialogProps, 'open' | 'onOpenChange'> & {
  dialogOpenState: boolean;
  toggleDrawer: any;
}) => {
  const { dialogOpenState, toggleDrawer } = props;

  const location = useLocation();
  const screenWidth = useScreenWidth();

  useEffect(() => {
    toggleDrawer(null);
  }, [location]);

  useEffect(() => {
    if (!dialogOpenState) return;

    toggleDrawer(null);
  }, [screenWidth]);

  return (
    <Dialog.Root {...props} open={dialogOpenState} modal={false}>
      {children}
    </Dialog.Root>
  );
};

export default function NavDrawer({
  className,
  navItem,
}: PropsWithNavItem & { className?: string }) {
  const loaderData = useLoaderData() as { isPlayerScreen?: boolean };
  const { openDrawer, toggleDrawer } = useDrawerStore();
  const isOpen = openDrawer === navItem.id;
  const isPlayerScreen = loaderData?.isPlayerScreen === true;

  const config = getPluginConfig<typeof manifest>(manifest, navItem);
  const showOverNavbar = config.styles?.show_over_navbar ?? false;

  const portalZIndex = getPortalZIndex({
    showOverNavbar,
    isPlayerScreen,
  });
  const portalClasses = [className, tailwindStyles.portal, portalZIndex]
    .filter(Boolean)
    .join(' ');

  return (
    <DrawerRoot dialogOpenState={isOpen} toggleDrawer={toggleDrawer}>
      <ToggleNavBarButton
        navItem={navItem}
        dialogOpenState={isOpen}
        toggleDrawer={toggleDrawer}
        isOpen={isOpen}
        isPlayerScreen={isPlayerScreen}
      />
      <Dialog.Portal>
        <Dialog.Content asChild>
          <div className={portalClasses}>
            <WrapperContent navItem={navItem} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </DrawerRoot>
  );
}
